import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import dayjs from 'dayjs';
import { getCompraIdentifier, IBeneficiados, ICompra } from '../models/compra.model';
import { ApplicationConfigService } from '../config/application-config.service';
import { createRequestOption } from '../request/request-util';
import { isPresent } from '../util/operators';
import { DATE_FORMAT } from '../util/input.constants';
import { EntityResponseTypeAny } from './certificado.service';
import { IEndereco } from '../models/endereco.model';
import { Account } from '../models/account.model';
import { FormaPagamento } from '../models/forma-pagamento.model';
import { ICompraByUser } from '../models/compra-by-user.model';
import { ICompraMultiDTO } from '../models/compraMultiDTO.model';


export type EntityResponseType = HttpResponse<ICompra>;
export type EntityArrayResponseType = HttpResponse<ICompra[]>;


@Injectable({ providedIn: 'root' })
export class CompraService {
  protected resourceUrl = this.applicationConfigService.getEndpointFor('api/compras');

  constructor(protected http: HttpClient, protected applicationConfigService: ApplicationConfigService) { }

  create(compra: ICompra): Observable<EntityResponseType> {
    const copy = this.convertDateFromClient(compra);
    return this.http
      .post<ICompra>(this.resourceUrl, copy, { observe: 'response' })
      .pipe(map((res: EntityResponseType) => this.convertDateFromServer(res)));
  }

  //   createB2C(compra: ICompra): Observable<EntityResponseType> {
  //     const copy = this.convertDateFromClient(compra);
  //     return this.http
  //       .post<ICompra>(`${this.resourceUrl}/b2c`, copy, { observe: 'response' })
  //       .pipe(map((res: EntityResponseType) => this.convertDateFromServer(res)));
  //   }

  createB2C(compra: ICompra, endereco?: IEndereco): Observable<EntityResponseTypeAny> {
    const copy = this.convertDateFromClient(compra);
    return this.http
      .post<any>(`${this.resourceUrl}/b2c`, { compra: copy, endereco }, { observe: 'response' })
      .pipe(map((res: EntityResponseType) => this.convertDateFromServer(res)))
  }

  createVendaFifo(compra: ICompra, formaDePagamento: FormaPagamento, endereco?: IEndereco, isPago?: boolean): Observable<EntityResponseTypeAny> {
    const copy = this.convertDateFromClient(compra);
    return this.http
      .post<any>(`${this.resourceUrl}/venda-aceitaram-termos`, { compra: copy, formaDePagamento, endereco, isPago }, { observe: 'response' })
      .pipe(map((res: EntityResponseType) => this.convertDateFromServer(res)))
  }

  createCompraProjeto(compraByUser: ICompraByUser): Observable<EntityResponseTypeAny> {
    const copy = this.convertDateFromClient(compraByUser.compra);
    compraByUser.compra = copy;
    return this.http
      .post<any>(`${this.resourceUrl}/projeto`, compraByUser, { observe: 'response' })
      .pipe(map((res: EntityResponseType) => this.convertDateFromServer(res)))
  }

  createCompraByUser(compra: ICompra, formaDePagamento: FormaPagamento, endereco?: IEndereco, produtor?: Account, isPago?: boolean): Observable<EntityResponseTypeAny> {
    const copy = this.convertDateFromClient(compra);
    return this.http
      .post<any>(`${this.resourceUrl}/byuser`, { compra: copy, produtor, formaDePagamento, endereco, isPago }, { observe: 'response' })
      .pipe(map((res: EntityResponseType) => this.convertDateFromServer(res)))
  }

  createMultiCompra(obj:ICompraMultiDTO): Observable<EntityResponseTypeAny>{
    return this.http
    .post<any>(`${this.resourceUrl}/multi-venda`,  obj, { observe: 'response' })
  }

  update(compra: ICompra): Observable<EntityResponseType> {
    const copy = this.convertDateFromClient(compra);
    return this.http
      .put<ICompra>(`${this.resourceUrl}/${getCompraIdentifier(compra) as number}`, copy, { observe: 'response' })
      .pipe(map((res: EntityResponseType) => this.convertDateFromServer(res)));
  }

  partialUpdate(compra: ICompra): Observable<EntityResponseType> {
    const copy = this.convertDateFromClient(compra);
    return this.http
      .patch<ICompra>(`${this.resourceUrl}/${getCompraIdentifier(compra) as number}`, copy, { observe: 'response' })
      .pipe(map((res: EntityResponseType) => this.convertDateFromServer(res)));
  }

  find(id: number): Observable<EntityResponseType> {
    return this.http
      .get<ICompra>(`${this.resourceUrl}/${id}`, { observe: 'response' })
      .pipe(map((res: EntityResponseType) => this.convertDateFromServer(res)));
  }

  query(req?: any): Observable<EntityArrayResponseType> {
    const options = createRequestOption(req);
    return this.http
      .get<ICompra[]>(this.resourceUrl, { params: options, observe: 'response' })
      .pipe(map((res: EntityArrayResponseType) => this.convertDateArrayFromServer(res)));
  }

  loadAllPaginated(res?: any): Observable<any>{
    return this.http
      .get<any>(`${this.resourceUrl}/pagination`, { params: res, observe: 'response' })
      .pipe(map((res: any) => this.convertPageFromServer(res)));
  }

  delete(id: number): Observable<HttpResponse<{}>> {
    return this.http.delete(`${this.resourceUrl}/${id}`, { observe: 'response' });
  }

  addCompraToCollectionIfMissing(compraCollection: ICompra[], ...comprasToCheck: (ICompra | null | undefined)[]): ICompra[] {
    const compras: ICompra[] = comprasToCheck.filter(isPresent);
    if (compras.length > 0) {
      const compraCollectionIdentifiers = compraCollection.map(compraItem => getCompraIdentifier(compraItem)!);
      const comprasToAdd = compras.filter(compraItem => {
        const compraIdentifier = getCompraIdentifier(compraItem);
        if (compraIdentifier == null || compraCollectionIdentifiers.includes(compraIdentifier)) {
          return false;
        }
        compraCollectionIdentifiers.push(compraIdentifier);
        return true;
      });
      return [...comprasToAdd, ...compraCollection];
    }
    return compraCollection;
  }


  protected convertDateFromClient(compra: ICompra): ICompra {
    return Object.assign({}, compra, {
      timestamp: compra.timestamp?.isValid() ? compra.timestamp.format(DATE_FORMAT) : undefined,
    });
  }

  protected convertDateFromServer(res: EntityResponseType): EntityResponseType {
    if (res.body) {
      res.body.timestamp = res.body.timestamp ? dayjs(res.body.timestamp) : undefined;
    }
    return res;
  }

  protected convertDateArrayFromServer(res: EntityArrayResponseType): EntityArrayResponseType {
    if (res.body) {
      res.body.forEach((compra: ICompra) => {
        compra.timestamp = compra.timestamp ? dayjs(compra.timestamp) : undefined;
      });
    }
    return res;
  }

  protected convertPageFromServer(res: any): any {
    if (res.body) {
      res.body.content.forEach((compra: ICompra) => {
        compra.timestamp = compra.timestamp ? dayjs(compra.timestamp) : undefined;
      });
    }
    return res;
  }

  listByUser(req?: any): Observable<EntityArrayResponseType> {
    const options = createRequestOption(req);
    return this.http
      .get<ICompra[]>(`${this.resourceUrl}/listbyuser`, { params: options, observe: 'response' })
      .pipe(map((res: EntityArrayResponseType) => this.convertDateArrayFromServer(res)));
  }

  findHistorico(id: number): Observable<EntityResponseTypeAny> {
    return this.http
      .get<any>(`${this.resourceUrl}/historico/${id}`, { observe: 'response' })
      .pipe(map((res: EntityResponseTypeAny) => res));
  }


  findProdutoresBeneficiadoCompra(res?: any): Observable<HttpResponse<any>> {
    return this.http
      .get<any>(`${this.resourceUrl}/relatorio-compras`, { params: res, observe: 'response' })
  }
}
